<template>
  <div v-if="vehicle" class="detail">
    <div class="detail__container detail__container--button">
      <button
        @click="navigateBack"
        class="detail__backButton button button--secondary button--arrowBack detail__no-print"
      >
        {{ $t('single.back') }}
      </button>
    </div>
    <!-- .container -->
    <div class="detail__titleContainer">
      <h1 class="detail__title">
        <strong>{{ vehicle.properties.brand }}</strong> {{ vehicle.properties.model }}
      </h1>
      <h2 class="detail__subtitle">{{ vehicle.properties.type | carSelexy }} {{ vehicle.properties.addition }}</h2>
    </div>
    <!-- .container -->
    <div class="detail__container">
      <div class="detail__main">
        <img v-if="vehicle.images.length > 0" :src="vehicle.images[0]" class="detail__print-only" />
        <div v-if="vehicle.images.length > 0 || vehicle.videos.length > 0" class="detail__slider detail__no-print">
          <slick
            class="detail__slider__main slick-main js-slick-main"
            ref="slickMain"
            @beforeChange="slickBeforeChange"
            :options="slickMainOptions"
          >
            <template v-for="video in vehicle.videos">
              <div :key="video.id" v-if="video.source === 'youtube'" class="slick-item-youtube">
                <iframe
                  width="100%"
                  height="100%"
                  :src="'https://www.youtube.com/embed/' + video.id + '?enablejsapi=1&version=3'"
                  frameborder="0"
                  allow="autoplay; encrypted-media"
                  allowfullscreen
                ></iframe>
              </div>
              <!-- .slick-item-youtube -->
              <div :key="video.id" v-if="video.source === 'taggle'" class="slick-item-taggle">
                <iframe
                  width="100%"
                  height="100%"
                  :src="'https://taggleauto.movieplayer.nl/' + video.id"
                  frameborder="0"
                  allow="autoplay; encrypted-media"
                  allowfullscreen
                ></iframe>
              </div>
              <!-- .slick-item-youtube -->
            </template>
            <div class="detail__slider__item" v-for="(image, index) in vehicle.images" :key="image">
              <img :src="image" alt="" />
              <button class="button button--expand detail__slider__expand" @click="openGallery(index)"></button>
            </div>
          </slick>
          <slick
            v-if="vehicle.images.length + vehicle.videos.length > 1"
            class="detail__slider__nav slick-nav js-slick-nav"
            ref="slickNav"
            :options="slickNavOptions"
          >
            <div v-for="video in vehicle.videos" :key="video.id" class="slick-nav-itemVideo">
              <img
                v-if="video.source === 'youtube'"
                :src="'https://img.youtube.com/vi/' + video.id + '/hqdefault.jpg'"
                alt=""
              />
              <img
                v-if="video.source === 'taggle'"
                :src="'https://taggleauto.movieplayer.nl/' + video.id + '/image.jpg'"
                alt=""
              />
            </div>
            <!-- .slick-nav-itemVideo -->
            <img v-for="image in vehicle.images" :key="image" :src="image" alt="" />
          </slick>
        </div>
        <!-- .vehicle__image -->
        <div v-else class="detail__imagePlaceholder"></div>

        <div class="detail__info detail__info--mobile">
          <h1 class="detail__info__title">
            <strong>{{ vehicle.properties.brand }}</strong> {{ vehicle.properties.model }}
          </h1>
          <h2 class="detail__info__subtitle">{{ vehicle.properties.type }}</h2>
          <div class="detail__info__priceWrapper">
            <span v-if="vehicle.properties.price" class="detail__info__price">
              {{ vehicle.properties.price | toEUR }}
              <span
                v-if="vehicle.properties.priceInclTax === false && vehicle.properties.btwMarge === 'BTW'"
                class="detail__info__price__tax"
                >{{ $t('vehicle.exclTax') }}</span
              >
            </span>
            <span
              v-if="!modules.financeCalculator.active && vehicle.properties.priceFinanceAmount"
              class="detail__info__monthlyPrice"
            >
              {{ $t('vehicle.finance.from') }} {{ vehicle.properties.priceFinanceAmount | round | toEUR }}
              {{ $t('vehicle.finance.perMonth') }}
            </span>
            <button
              v-if="
                modules.financeCalculator.active && vehicle.properties.price && vehicle.properties.priceFinanceAmount
              "
              class="detail__info__monthlyPrice detail__info__monthlyPrice--asLink"
              @click="openModal('financeCalculator')"
            >
              {{ $t('vehicle.finance.from') }} {{ vehicle.properties.priceFinanceAmount | round | toEUR }}
              {{ $t('vehicle.finance.perMonth') }}
            </button>

            <button
              class="detail__info__monthlyPrice detail__info__monthlyPrice--asLink"
              v-if="modules.privateLease.active && vehicle.properties.pricePrivateLeaseAmount"
              @click="openModal('privateLease')"
            >
              {{ $t('vehicle.finance.privateLeaseFrom') }}
              {{ vehicle.properties.pricePrivateLeaseAmount | round | toEUR }} {{ $t('vehicle.finance.perMonth') }}
            </button>
          </div>
          <!-- .detail__info__priceWrapper -->
          <ul class="detail__info__list">
            <li v-if="vehicle.properties.year" class="detail__info__item detail__info__item--year">
              <span class="detail__info__label">{{ $t('vehicle.year') }}</span>
              <span class="detail__info__value">{{ vehicle.properties.year }}</span>
            </li>
            <!-- .detail__info__item -->
            <li v-if="vehicle.properties.mileage" class="detail__info__item detail__info__item--mileage">
              <span class="detail__info__label">{{ $t('vehicle.mileage') }}</span>
              <span class="detail__info__value"
                >{{ vehicle.properties.mileage | thousandSeparated }} {{ vehicle.properties.mileageUnit }}</span
              >
            </li>
            <!-- .detail__info__item -->
          </ul>
          <!-- .detail__info__list -->
          <ul class="detail__info__list">
            <li v-if="vehicle.properties.fuelType" class="detail__info__item detail__info__item--fuelType">
              <span class="detail__info__label">{{ $t('vehicle.fuelType') }}</span>
              <span class="detail__info__value">{{ vehicle.properties.fuelType }}</span>
            </li>
            <!-- .detail__info__item -->
            <li v-if="vehicle.properties.transmission" class="detail__info__item detail__info__item--transmission">
              <span class="detail__info__label">{{ $t('vehicle.transmission') }}</span>
              <span class="detail__info__value">{{ vehicle.properties.transmission }}</span>
            </li>
            <!-- .detail__info__item -->
          </ul>
          <!-- .detail__info__list -->
        </div>
        <!-- .detail__info.detail__info--main -->

        <div class="detail__actionButtons detail__actionButtons--mobile">
          <button
            v-if="modules.buy.active"
            class="detail__button button button--arrow"
            :class="[modules.buy.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('buy')"
          >
            {{ $t('vehicle.actions.buy') }}
          </button>
          <button
            v-if="modules.financeCalculator.active && vehicle.properties.price"
            class="detail__button button button--arrow"
            :class="[modules.financeCalculator.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('financeCalculator')"
          >
            {{ $t('vehicle.actions.finance') }}
          </button>
          <button
            v-if="modules.privateLease.active"
            class="detail__button button button--arrow"
            :class="[modules.privateLease.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('privateLease')"
          >
            {{ $t('vehicle.actions.privateLease') }}
          </button>
          <button
            v-if="modules.taxtool.active"
            class="detail__button button button--arrow"
            :class="[modules.taxtool.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('taxTool')"
          >
            {{ $t('vehicle.actions.tradeIn') }}
          </button>
          <button
            v-if="modules.appointment.active"
            class="detail__button button button--arrow"
            :class="[modules.appointment.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('appointment')"
          >
            {{ $t('vehicle.actions.appointment') }}
          </button>
          <button
            v-if="modules.testRide.active"
            class="detail__button button button--arrow"
            :class="[modules.testRide.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('testRide')"
          >
            {{ $t('vehicle.actions.testDrive') }}
          </button>
          <button
            v-if="modules.offer && modules.offer.active"
            :class="[modules.offer.highlighted ? 'button--primary' : 'button--secondary']"
            class="detail__button button button--arrow"
            @click="openModal('offer')"
          >
            {{ $t('vehicle.actions.offer') }}
          </button>
          <button
            v-if="modules.callMeBack.active"
            class="detail__button button button--arrow"
            :class="[modules.callMeBack.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('callMeBack')"
          >
            {{ $t('vehicle.actions.callMeBack') }}
          </button>
          <button
            v-if="generalSettings.showCompare"
            @click="addVehicle(vehicle)"
            class="detail__button detail__button--last button button--secondary button--compare"
          >
            {{ $t('vehicle.actions.compare') }}
          </button>
          <!-- .detail__actionButtons -->

          <button
            v-if="generalSettings.showCompare"
            v-on:click="addVehicle(vehicle)"
            class="detail__button button button--secondary button--compare"
          >
            Vergelijken
          </button>
        </div>
        <!-- .detail__actionButtons.detail__actionButtons--main -->

        <div
          v-if="modules.buy.active && modules.buy.packages.length > 0"
          class="detail__delivery-packages detail__delivery-packages--mobile"
        >
          <span class="detail__delivery-packages__title">{{ $t('single.tabs.deliverOptions') }}</span>
          <div
            v-for="pack in getPackagesInLanguage"
            :key="pack.delivery_name"
            class="detail__delivery-packages__container"
          >
            <div
              v-if="pack.delivery_price && parseInt(pack.delivery_price) > 0"
              class="detail__delivery-packages__package"
            >
              <div class="detail__delivery-packages__header">
                <span class="detail__delivery-packages__name">{{ pack.delivery_name }}</span>
                <span class="detail__delivery-packages__price">&euro; {{ pack.delivery_price }}</span>
              </div>
              <p class="detail__delivery-packages__desc">{{ pack.delivery_desc }}</p>
            </div>
          </div>
        </div>

        <div class="detail__tabbedInfo detail__no-print">
          <ul class="detail__tabs">
            <li class="detail__tabs__item" :class="{ '-active': activeTab === 'specs' }" @click="setTab('specs')">
              {{ $t('single.tabs.specs') }}
            </li>
            <li class="detail__tabs__item" :class="{ '-active': activeTab === 'options' }" @click="setTab('options')">
              {{ $t('single.tabs.options') }}
            </li>
            <li
              v-if="vehicle.properties.description"
              class="detail__tabs__item"
              :class="{ '-active': activeTab === 'description' }"
              @click="setTab('description')"
            >
              {{ $t('single.tabs.desc') }}
            </li>
            <li
              v-if="warrantyTabActive"
              class="detail__tabs__item"
              :class="{ '-active': activeTab === 'warranty' }"
              @click="setTab('warranty')"
            >
              {{ $t('single.tabs.warranty') }}
            </li>
            <li
              v-if="generalSettings.showDeliverOptions && vehicle.properties.deliverOptions.length"
              class="detail__tabs__item"
              :class="{ '-active': activeTab === 'deliverOptions' }"
              @click="setTab('deliverOptions')"
            >
              {{ $t('single.tabs.deliverOptions') }}
            </li>
          </ul>

          <div class="detail__tabContent">
            <!-- SPECS -->
            <div v-if="activeTab === 'specs'">
              <h2 class="detail__tabTitle">{{ $t('single.tabs.specs') }}</h2>
              <dl class="specList">
                <template>
                  <dt class="specList__term">{{ $t('vehicle.mileage') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.mileage | thousandSeparated }}
                    {{ vehicle.properties.mileageUnit ? vehicle.properties.mileageUnit : $t('vehicle.mileageUnit') }}
                    <img
                      v-if="vehicle.properties.warranty.nap"
                      class="detail__inlineNapLogo"
                      :src="WPSettings.ASSETS_DIR + '/logo-nap.jpg'"
                      alt="NAP-Check"
                    />
                  </dd>
                </template>

                <template v-if="vehicle.properties.enginePowerKW && vehicle.properties.enginePowerPK">
                  <dt class="specList__term">{{ $t('vehicle.enginePower') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.enginePowerKW }} {{ $t('vehicle.energyPowerUnit') }} ({{
                      vehicle.properties.enginePowerPK
                    }}
                    {{ $t('vehicle.horsePowerUnit') }})
                  </dd>
                </template>

                <template v-if="vehicle.properties.license">
                  <dt class="specList__term">{{ $t('vehicle.license') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.license }}</dd>
                </template>

                <template v-if="vehicle.properties.body">
                  <dt class="specList__term">{{ $t('vehicle.body') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.body }}</dd>
                </template>

                <template v-if="vehicle.properties.year">
                  <dt class="specList__term">{{ $t('vehicle.year') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.year }}</dd>
                </template>

                <template v-if="vehicle.properties.numberDoors">
                  <dt class="specList__term">{{ $t('vehicle.numberDoors') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.numberDoors }}</dd>
                </template>

                <template v-if="vehicle.properties.lining">
                  <dt class="specList__term">{{ $t('vehicle.lining') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.lining }}</dd>
                </template>

                <template v-if="vehicle.properties.fuelType">
                  <dt class="specList__term">{{ $t('vehicle.fuelType') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.fuelType }}</dd>
                </template>

                <template v-if="vehicle.properties.transmission">
                  <dt class="specList__term">{{ $t('vehicle.transmission') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.transmission }}</dd>
                </template>

                <template v-if="vehicle.properties.color">
                  <dt class="specList__term">{{ $t('vehicle.color') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.color }}</dd>
                </template>

                <template v-if="vehicle.properties.numberCilinders">
                  <dt class="specList__term">{{ $t('vehicle.numberCilinders') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.numberCilinders }}</dd>
                </template>

                <template
                  v-if="
                    vehicle.properties.size.length && vehicle.properties.size.width && vehicle.properties.size.height
                  "
                >
                  <dt class="specList__term">{{ $t('vehicle.sizes') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.size.length }} x {{ vehicle.properties.size.width }} x
                    {{ vehicle.properties.size.height }}
                  </dd>
                </template>

                <template v-if="vehicle.properties.engineCapacity">
                  <dt class="specList__term">{{ $t('vehicle.engineCapacity') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.engineCapacity }} {{ $t('vehicle.engineCapacityUnit') }}
                  </dd>
                </template>

                <template v-if="vehicle.properties.mass">
                  <dt class="specList__term">{{ $t('vehicle.weight') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.mass }} {{ $t('vehicle.weightUnit') }}</dd>
                </template>

                <template v-if="vehicle.properties.towingWeight">
                  <dt class="specList__term">{{ $t('vehicle.maxTowingWeight') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.towingWeight }} {{ $t('vehicle.weightUnit') }}</dd>
                </template>

                <template v-if="vehicle.properties.engineCapacity">
                  <dt class="specList__term">{{ $t('vehicle.engineVolume') }}</dt>
                  <dd class="specList__desc">
                    {{ (vehicle.properties.engineCapacity / 1000) | round(1) }} {{ $t('vehicle.volumeUnit') }}
                  </dd>
                </template>

                <template v-if="vehicle.properties.averageFuelConsumption">
                  <dt class="specList__term">{{ $t('vehicle.fuelConsumption', { amount: 100 }) }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.averageFuelConsumption }} L</dd>
                </template>

                <template v-if="vehicle.properties.btwMarge">
                  <dt class="specList__term">{{ $t('vehicle.taxMargin') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.btwMarge }}</dd>
                </template>

                <template v-if="vehicle.properties.energyLabel">
                  <dt class="specList__term">{{ $t('vehicle.energyLabel') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.energyLabel }}</dd>
                </template>

                <template v-if="vehicle.properties.quarterlyRoadTax.min && vehicle.properties.quarterlyRoadTax.max">
                  <dt class="specList__term">{{ $t('vehicle.roadTax') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.quarterlyRoadTax.min | toEUR }} -
                    {{ vehicle.properties.quarterlyRoadTax.max | toEUR }}
                    {{ $t('vehicle.perQuarterEstimation') }}
                  </dd>
                </template>
              </dl>
              <!-- specList -->
            </div>

            <!-- OPTIONS -->
            <div v-if="activeTab === 'options'">
              <h2 class="detail__tabTitle">{{ $t('single.tabs.options') }}</h2>
              <ul class="optionList">
                <li
                  class="optionList__item"
                  v-for="option in vehicle.properties.options"
                  :key="option"
                  v-html="option"
                ></li>
              </ul>
            </div>

            <!-- DESCRIPTION -->
            <div v-if="activeTab === 'description'">
              <h2 class="detail__tabTitle">{{ $t('single.tabs.desc') }}</h2>
              <p v-html="vehicle.properties.description"></p>
            </div>

            <!-- WARRANTY -->
            <div v-if="activeTab === 'warranty'">
              <h2 class="detail__tabTitle">{{ $t('single.tabs.warranty') }}</h2>
              <ul class="detail__warrantyList">
                <li v-if="vehicle.properties.warranty.nap" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-nap.jpg'"
                    alt="NAP-Check"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.napCheck') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.honderdProcentOnderhouden" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-honderdprocentonderhouden.svg'"
                    alt="100% onderhouden"
                  />
                  <span class="detail__warrantyList__text">{{
                    $t('vehicle.warranties.hundredPercentMaintained')
                  }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.carpass" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-carpass.jpg'"
                    alt="Car-Pass"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.carPass') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->
                <li v-if="vehicle.properties.warranty.bovag40puntencheck" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-bovag.jpg'"
                    alt="BOVAG 40 punten check"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.bovag40') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.bovagAfleverbeurt" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-bovag.jpg'"
                    alt="BOVAG Afleverbeurt"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.bovagAfleverbeurt') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.bovagGarantie6" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-bovag-garantie.jpg'"
                    alt="BOVAG Garantie"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.bovag6months') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.bovagGarantie12" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-bovag-garantie.jpg'"
                    alt="BOVAG Garantie"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.bovag12months') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.bovagGarantie24" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-bovag-garantie.jpg'"
                    alt="BOVAG Garantie"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.bovag24months') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.autotrust" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-autotrust.svg'"
                    alt="Autotrust"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.autotrust') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.vakgarant" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-vakgarant.jpg'"
                    alt="Vakgarant"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.vakgarant') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.vweOccasionGarant" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-vweoccasiongarant.jpg'"
                    alt="VWE Occasion Garant Plan"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.vweOccasionPlan') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->

                <li v-if="vehicle.properties.warranty.cargarantie" class="detail__warrantyList__item">
                  <img
                    class="detail__warrantyList__image"
                    :src="WPSettings.ASSETS_DIR + '/logo-cargarantie.png'"
                    alt="Cargarantie"
                  />
                  <span class="detail__warrantyList__text">{{ $t('vehicle.warranties.carGarantie') }}</span>
                </li>
                <!-- .detail__warrantyList__item -->
              </ul>
              <!-- .detail__warrantyList -->

              <ul v-if="generalSettings.warranties.length > 0" class="detail__warrantyListCustom">
                <li v-for="warranty in generalSettings.warranties" class="detail__warrantyListCustom__item">
                  <img
                    v-if="warranty.logo"
                    class="detail__warrantyListCustom__image"
                    :src="warranty.logo"
                    :alt="warranty.name"
                  />
                  <div>
                    <div class="detail__warrantyListCustom__title">{{ warranty.name }}</div>
                    <p v-if="warranty.desc" class="detail__warrantyListCustom__text">{{ warranty.desc }}</p>
                    <a
                      v-if="warranty.url"
                      class="detail__warrantyListCustom__link"
                      :href="warranty.url"
                      target="_blank"
                      >{{ $t('single.warranty.moreInfo') }}</a
                    >
                  </div>
                </li>
                <!-- .detail__warrantyList__item -->
              </ul>
              <!-- .detail__customWarranties -->

              <div v-if="generalSettings.additionalWarranty.active" class="detail__additionalWarranty">
                <a
                  :href="generalSettings.additionalWarranty.url"
                  class="detail__button button button--arrow button--primary"
                  target="_blank"
                >
                  {{ $t('vehicle.warranties.additionalWarrantyButton') }}
                </a>
              </div>
              <!-- .detail__additionalWarranty -->
            </div>

            <!-- DESCRIPTION -->
            <template v-if="activeTab === 'deliverOptions'">
              <div
                class="detail__deliverOption"
                v-for="deliverOption in vehicle.properties.deliverOptions"
                :key="deliverOption"
              >
                <h2 class="detail__tabTitle">{{ deliverOption.name }}</h2>
                <dl class="specList specList--largeDesc">
                  <template v-if="deliverOption.description">
                    <dt class="specList__term">{{ $t('single.deliverOptions.description') }}</dt>
                    <dd class="specList__desc" v-html="deliverOption.description"></dd>
                  </template>

                  <template v-if="deliverOption.priceIn">
                    <dt class="specList__term">{{ $t('single.deliverOptions.price') }}</dt>
                    <dd class="specList__desc">
                      {{ deliverOption.priceIn | toEUR }} {{ $t('single.deliverOptions.inclTax') }}
                    </dd>
                  </template>

                  <template v-if="deliverOption.items && deliverOption.items.length">
                    <dt class="specList__term">{{ $t('single.deliverOptions.contents') }}</dt>
                    <dd class="specList__desc">
                      <ul>
                        <li v-for="item in deliverOption.items" :key="item" v-html="item"></li>
                      </ul>
                    </dd>
                  </template>
                </dl>
              </div>
              <!-- .deliverOptionsTable -->
            </template>
          </div>
          <!-- .detail__tabContent -->
        </div>
        <!-- .detail__tabbedInfo -->
      </div>
      <!-- .detail__main -->

      <div class="detail__aside">
        <div class="detail__info detail__info--desktop">
          <div class="detail__info__priceWrapper">
            <span v-if="vehicle.properties.price" class="detail__info__price"
              >{{ vehicle.properties.price | toEUR }}
              <span
                v-if="vehicle.properties.priceInclTax === false && vehicle.properties.btwMarge === 'BTW'"
                class="detail__info__price__tax"
                >{{ $t('vehicle.exclTax') }}</span
              >
            </span>

            <span
              v-if="!modules.financeCalculator.active && vehicle.properties.priceFinanceAmount"
              class="detail__info__monthlyPrice"
            >
              {{ $t('vehicle.finance.from') }} {{ vehicle.properties.priceFinanceAmount | round | toEUR }}
              {{ $t('vehicle.finance.perMonth') }}
            </span>
            <button
              v-if="
                modules.financeCalculator.active && vehicle.properties.price && vehicle.properties.priceFinanceAmount
              "
              class="detail__info__monthlyPrice detail__info__monthlyPrice--asLink"
              @click="openModal('financeCalculator')"
            >
              {{ $t('vehicle.finance.from') }} {{ vehicle.properties.priceFinanceAmount | round | toEUR }}
              {{ $t('vehicle.finance.perMonth') }}
            </button>

            <button
              class="detail__info__monthlyPrice detail__info__monthlyPrice--asLink"
              v-if="modules.privateLease.active && vehicle.properties.pricePrivateLeaseAmount"
              @click="openModal('privateLease')"
            >
              {{ $t('vehicle.finance.privateLeaseFrom') }}
              {{ vehicle.properties.pricePrivateLeaseAmount | round | toEUR }} {{ $t('vehicle.finance.perMonth') }}
            </button>
          </div>
          <!-- .detail__info__priceWrapper -->
          <ul class="detail__info__list">
            <li v-if="vehicle.properties.year" class="detail__info__item detail__info__item--year">
              <span class="detail__info__label">{{ $t('vehicle.year') }}</span>
              <span class="detail__info__value">{{ vehicle.properties.year }}</span>
            </li>
            <!-- .detail__info__item -->
            <li v-if="vehicle.properties.mileage" class="detail__info__item detail__info__item--mileage">
              <span class="detail__info__label">{{ $t('vehicle.mileage') }}</span>
              <span class="detail__info__value"
                >{{ vehicle.properties.mileage | thousandSeparated }} {{ vehicle.properties.mileageUnit }}</span
              >
            </li>
            <!-- .detail__info__item -->
            <li v-if="vehicle.properties.fuelType" class="detail__info__item detail__info__item--fuelType">
              <span class="detail__info__label">{{ $t('vehicle.fuelType') }}</span>
              <span class="detail__info__value">{{ vehicle.properties.fuelType }}</span>
            </li>
            <!-- .detail__info__item -->
            <li v-if="vehicle.properties.transmission" class="detail__info__item detail__info__item--transmission">
              <span class="detail__info__label">{{ $t('vehicle.transmission') }}</span>
              <span class="detail__info__value">{{ vehicle.properties.transmission }}</span>
            </li>
            <!-- .detail__info__item -->
          </ul>
          <!-- .detail__info__list -->
        </div>
        <!-- .detail__info -->

        <div class="detail__print-only">
          <h2 class="detail__tabTitle">{{ $t('single.tabs.specs') }}</h2>
          <div class="detail__specList">
            <div>
              <dl class="specList">
                <template>
                  <dt class="specList__term">{{ $t('vehicle.mileage') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.mileage | thousandSeparated }}
                    {{ vehicle.properties.mileageUnit ? vehicle.properties.mileageUnit : $t('vehicle.mileageUnit') }}
                    <img
                      v-if="vehicle.properties.warranty.nap"
                      class="detail__inlineNapLogo"
                      :src="WPSettings.ASSETS_DIR + '/logo-nap.jpg'"
                      alt="NAP-Check"
                    />
                  </dd>
                </template>

                <template v-if="vehicle.properties.enginePowerKW && vehicle.properties.enginePowerPK">
                  <dt class="specList__term">{{ $t('vehicle.enginePower') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.enginePowerKW }} {{ $t('vehicle.energyPowerUnit') }} ({{
                      vehicle.properties.enginePowerPK
                    }}
                    {{ $t('vehicle.horsePowerUnit') }})
                  </dd>
                </template>

                <template v-if="vehicle.properties.license">
                  <dt class="specList__term">{{ $t('vehicle.license') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.license }}</dd>
                </template>

                <template v-if="vehicle.properties.body">
                  <dt class="specList__term">{{ $t('vehicle.body') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.body }}</dd>
                </template>

                <template v-if="vehicle.properties.year">
                  <dt class="specList__term">{{ $t('vehicle.year') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.year }}</dd>
                </template>

                <template v-if="vehicle.properties.numberDoors">
                  <dt class="specList__term">{{ $t('vehicle.numberDoors') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.numberDoors }}</dd>
                </template>

                <template v-if="vehicle.properties.lining">
                  <dt class="specList__term">{{ $t('vehicle.lining') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.lining }}</dd>
                </template>
              </dl>
            </div>
            <div>
              <dl class="specList">
                <template v-if="vehicle.properties.fuelType">
                  <dt class="specList__term">{{ $t('vehicle.fuelType') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.fuelType }}</dd>
                </template>

                <template v-if="vehicle.properties.transmission">
                  <dt class="specList__term">{{ $t('vehicle.transmission') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.transmission }}</dd>
                </template>

                <template v-if="vehicle.properties.color">
                  <dt class="specList__term">{{ $t('vehicle.color') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.color }}</dd>
                </template>
                <template v-if="vehicle.properties.numberCilinders">
                  <dt class="specList__term">{{ $t('vehicle.numberCilinders') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.numberCilinders }}</dd>
                </template>

                <template
                  v-if="
                    vehicle.properties.size.length && vehicle.properties.size.width && vehicle.properties.size.height
                  "
                >
                  <dt class="specList__term">{{ $t('vehicle.sizes') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.size.length }} x {{ vehicle.properties.size.width }} x
                    {{ vehicle.properties.size.height }}
                  </dd>
                </template>

                <template v-if="vehicle.properties.engineCapacity">
                  <dt class="specList__term">{{ $t('vehicle.engineCapacity') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.engineCapacity }} {{ $t('vehicle.engineCapacityUnit') }}
                  </dd>
                </template>

                <template v-if="vehicle.properties.mass">
                  <dt class="specList__term">{{ $t('vehicle.weight') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.mass }} {{ $t('vehicle.weightUnit') }}</dd>
                </template>
              </dl>
            </div>
            <div>
              <dl class="specList">
                <template v-if="vehicle.properties.towingWeight">
                  <dt class="specList__term">{{ $t('vehicle.maxTowingWeight') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.towingWeight }} {{ $t('vehicle.weightUnit') }}</dd>
                </template>

                <template v-if="vehicle.properties.engineCapacity">
                  <dt class="specList__term">{{ $t('vehicle.engineVolume') }}</dt>
                  <dd class="specList__desc">
                    {{ (vehicle.properties.engineCapacity / 1000) | round(1) }} {{ $t('vehicle.volumeUnit') }}
                  </dd>
                </template>

                <template v-if="vehicle.properties.averageFuelConsumption">
                  <dt class="specList__term">{{ $t('vehicle.fuelConsumption', { amount: 100 }) }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.averageFuelConsumption }} L</dd>
                </template>

                <template v-if="vehicle.properties.btwMarge">
                  <dt class="specList__term">{{ $t('vehicle.taxMargin') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.btwMarge }}</dd>
                </template>

                <template v-if="vehicle.properties.energyLabel">
                  <dt class="specList__term">{{ $t('vehicle.energyLabel') }}</dt>
                  <dd class="specList__desc">{{ vehicle.properties.energyLabel }}</dd>
                </template>

                <template v-if="vehicle.properties.quarterlyRoadTax.min && vehicle.properties.quarterlyRoadTax.max">
                  <dt class="specList__term">{{ $t('vehicle.roadTax') }}</dt>
                  <dd class="specList__desc">
                    {{ vehicle.properties.quarterlyRoadTax.min | toEUR }} -
                    {{ vehicle.properties.quarterlyRoadTax.max | toEUR }}
                    {{ $t('vehicle.perQuarterEstimation') }}
                  </dd>
                </template>
              </dl>
            </div>
          </div>
        </div>
        <!-- .print-only tabs -->

        <div class="detail__print-only">
          <h2 class="detail__tabTitle">{{ $t('single.tabs.options') }}</h2>
          <ul class="optionList">
            <li
              class="optionList__item"
              v-for="option in vehicle.properties.options"
              :key="option"
              v-html="option"
            ></li>
          </ul>
        </div>
        <!-- .print-only tabs -->

        <div class="detail__print-only">
          <h2 class="detail__tabTitle">{{ $t('single.tabs.desc') }}</h2>
          <p v-html="vehicle.properties.description"></p>
        </div>
        <!-- .print-only tabs -->

        <div class="detail__actionButtons detail__actionButtons--desktop" ref="actionButtons">
          <button
            v-if="modules.buy.active"
            class="detail__button button button--arrow"
            :class="[modules.buy.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('buy')"
          >
            {{ $t('vehicle.actions.buy') }}
          </button>
          <button
            v-if="modules.financeCalculator.active && vehicle.properties.price"
            class="detail__button button button--arrow"
            :class="[modules.financeCalculator.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('financeCalculator')"
          >
            {{ $t('vehicle.actions.finance') }}
          </button>
          <button
            v-if="modules.privateLease.active"
            class="detail__button button button--arrow"
            :class="[modules.privateLease.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('privateLease')"
          >
            {{ $t('vehicle.actions.privateLease') }}
          </button>
          <button
            v-if="modules.taxtool.active"
            class="detail__button button button--arrow"
            :class="[modules.taxtool.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('taxTool')"
          >
            {{ $t('vehicle.actions.tradeIn') }}
          </button>
          <button
            v-if="modules.appointment.active"
            class="detail__button button button--arrow"
            :class="[modules.appointment.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('appointment')"
          >
            {{ $t('vehicle.actions.appointment') }}
          </button>
          <button
            v-if="modules.testRide.active"
            class="detail__button button button--arrow"
            :class="[modules.testRide.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('testRide')"
          >
            {{ $t('vehicle.actions.testDrive') }}
          </button>
          <button
            v-if="modules.offer && modules.offer.active"
            :class="[modules.offer.highlighted ? 'button--primary' : 'button--secondary']"
            class="detail__button button button--arrow"
            @click="openModal('offer')"
          >
            {{ $t('vehicle.actions.offer') }}
          </button>
          <button
            v-if="modules.callMeBack.active"
            class="detail__button button button--arrow"
            :class="[modules.callMeBack.highlighted ? 'button--primary' : 'button--secondary']"
            @click="openModal('callMeBack')"
          >
            {{ $t('vehicle.actions.callMeBack') }}
          </button>
          <button
            v-if="generalSettings.showCompare"
            @click="addVehicle(vehicle)"
            class="detail__button detail__button--last button button--secondary button--compare"
          >
            {{ $t('vehicle.actions.compare') }}
          </button>
          <button class="detail__button button button--secondary button--print" @click="printVehicle">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#737373" width="14" height="14">
              <path
                fill-rule="evenodd"
                d="M5 2.75C5 1.784 5.784 1 6.75 1h6.5c.966 0 1.75.784 1.75 1.75v3.552c.377.046.752.097 1.126.153A2.212 2.212 0 0118 8.653v4.097A2.25 2.25 0 0115.75 15h-.241l.305 1.984A1.75 1.75 0 0114.084 19H5.915a1.75 1.75 0 01-1.73-2.016L4.492 15H4.25A2.25 2.25 0 012 12.75V8.653c0-1.082.775-2.034 1.874-2.198.374-.056.75-.107 1.127-.153L5 6.25v-3.5zm8.5 3.397a41.533 41.533 0 00-7 0V2.75a.25.25 0 01.25-.25h6.5a.25.25 0 01.25.25v3.397zM6.608 12.5a.25.25 0 00-.247.212l-.693 4.5a.25.25 0 00.247.288h8.17a.25.25 0 00.246-.288l-.692-4.5a.25.25 0 00-.247-.212H6.608z"
                clip-rule="evenodd"
              />
            </svg>
            <span>
              {{ $t('vehicle.actions.print') }}
            </span>
          </button>
        </div>
        <!-- .detail__actionButtons -->

        <div v-if="companyInfo" class="detail__companyInfo detail__no-print">
          <p>
            <span class="detail__companyInfo__title">{{ companyInfo.name }}</span>
            <span class="detail__companyInfo__text">{{ companyInfo.street }}</span>
            <span class="detail__companyInfo__text">{{ companyInfo.zip }} {{ companyInfo.city }}</span>
            <span class="detail__companyInfo__text">{{ companyInfo.phone }}</span>
          </p>
          <div class="detail__companyInfo__contact">
            <a
              :href="`https://www.google.com/maps/dir/?api=1&destination=${companyInfo.street},${companyInfo.zip}%20${companyInfo.city}`"
              target="_blank"
            >
              <icon-map-pin-outline class="button-icon" />
              <span>
                {{ $t('single.route') }}
              </span>
            </a>
            <a v-if="companyInfo.email" :href="`mailto:${companyInfo.email}`" target="_blank">
              <icon-envelope-outline class="button-icon" />
              <span>
                {{ $t('single.email') }}
              </span>
            </a>
            <a v-if="companyInfo.whatsapp" :href="companyInfo.whatsapp | whatsapp" target="_blank">
              <icon-whatsapp-outline class="button-icon" style="width: 16px; height: 16px" />
              <span> WhatsApp </span>
            </a>
          </div>
        </div>
        <!-- .detail__company -->

        <div v-if="generalSettings.contactInfo.name" class="detail__contactInfo detail__no-print">
          <span class="detail__contactInfo__title">{{ $t('single.contactTitle') }}</span>

          <div class="detail__contactInfo__wrapper">
            <img
              class="detail__contactInfo__avatar"
              v-if="generalSettings.contactInfo.image"
              :src="generalSettings.contactInfo.image"
              :alt="'Contact ' + generalSettings.contactInfo.name"
            />
            <div class="detail__contactInfo__content">
              <p>
                <strong>{{ generalSettings.contactInfo.name }}</strong
                ><br />
                <em v-if="generalSettings.contactInfo.jobTitle" class="detail__contactInfo__position">{{
                  generalSettings.contactInfo.jobTitle
                }}</em>
              </p>
              <a :href="'mailto:' + generalSettings.contactInfo.email">{{ generalSettings.contactInfo.email }}</a
              ><br />
              <a v-if="generalSettings.contactInfo.phone" :href="'tel:' + generalSettings.contactInfo.phone">{{
                generalSettings.contactInfo.phone
              }}</a>
            </div>
            <!-- .detail__contactInfo__text -->
          </div>
          <!-- .detail__contactInfo__wrapper -->
        </div>
        <!-- .detail__contactInfo -->

        <div class="detail__share detail__no-print">
          <span class="detail__share__title">{{ $t('single.shareTitle') }}</span>

          <div class="detail__share__buttons">
            <a
              :href="
                ('https://twitter.com/intent/tweet?text=' + $t('single.shareText') + companyInfo.name + '%20' + pageUrl)
                  | urlFormat
              "
              target="_blank"
              class="detail__share__button detail__share__button--twitter"
            ></a>

            <a
              :href="'https://www.facebook.com/sharer/sharer.php?u=' + pageUrl"
              target="_blank"
              class="detail__share__button detail__share__button--facebook"
            ></a>

            <a
              :href="('https://wa.me?text=' + $t('single.shareText') + companyInfo.name + '%20' + pageUrl) | urlFormat"
              target="_blank"
              class="detail__share__button detail__share__button--whatsapp"
            ></a>

            <a
              :href="('mailto:?subject=' + $t('single.shareText') + companyInfo.name + '&body=' + pageUrl) | urlFormat"
              target="_blank"
              class="detail__share__button detail__share__button--email"
            ></a>
          </div>
          <!-- .detail__share__buttons -->
        </div>
        <div
          v-if="generalSettings.deliveries.length > 0"
          class="detail__delivery-packages detail__delivery-packages--desktop"
        >
          <span class="detail__delivery-packages__title">{{ $t('single.tabs.deliverOptions') }}</span>
          <div
            v-for="pack in getPackagesInLanguage"
            :key="pack.delivery_name"
            class="detail__delivery-packages__container"
          >
            <div
              v-if="(pack.delivery_price && parseInt(pack.delivery_price) > 0) || (pack.delivery_desc !== '')"
              class="detail__delivery-packages__package"
            >
              <div class="detail__delivery-packages__header">
                <span class="detail__delivery-packages__name">{{ pack.delivery_name }}</span>
                <span class="detail__delivery-packages__price">&euro; {{ pack.delivery_price }}</span>
              </div>
              <p class="detail__delivery-packages__desc">{{ pack.delivery_desc }}</p>
            </div>
          </div>
        </div>
        <!-- .detail__company -->
      </div>
      <!-- .detail__aside -->
    </div>
    <!-- .detail -->

    <CoolLightBox
      ref="lightbox"
      :items="lightboxMedia"
      :index="lightboxIndex"
      :slideshow="false"
      loop
      :z-index="9999999"
      @close="lightboxIndex = null"
      :overlayColor="'rgba(0,0,0,.85)'"
    >
    </CoolLightBox>

    <!-- MODAL OFFER -->
    <ModalOffer
      v-if="modules.offer && modules.offer.active"
      :intro-text="modules.offer.introText"
      :active="activeModal === 'offer'"
      :vehicle="vehicle"
      @modalClosed="closeModal"
    ></ModalOffer>

    <!-- MODAL TESTRIDE -->
    <ModalTestRide
      v-if="modules.testRide.active"
      :active="activeModal === 'testRide'"
      :intro-text="modules.testRide.introText"
      :vehicle="vehicle"
      @modalClosed="closeModal"
    >
    </ModalTestRide>

    <!-- MODAL APPOINTMENT -->
    <ModalAppointment
      v-if="modules.appointment.active"
      :intro-text="modules.appointment.introText"
      :active="activeModal === 'appointment'"
      :vehicle="vehicle"
      @modalClosed="closeModal"
    >
    </ModalAppointment>

    <!-- MODAL LEASE CALCULATOR -->
    <ModalPrivateLease
      v-if="modules.privateLease.active && activeModal === 'privateLease'"
      :active="activeModal === 'privateLease'"
      :intro-text="modules.privateLease.introText"
      :vehicle="vehicle"
      @modalClosed="closeModal"
    ></ModalPrivateLease>

    <!-- MODAL FINANCE CALCULATOR -->
    <ModalCalculator
      v-if="modules.financeCalculator.active"
      :active="activeModal === 'financeCalculator'"
      :toolType="modules.financeCalculator.toolType"
      :minFinanceValueExclTax="modules.financeCalculator.minFinanceValue"
      :interestRate="modules.financeCalculator.interestRate"
      :leasetoolDealerId="modules.financeCalculator.leasetoolDealerId"
      :rosfinanceAPIKey="modules.financeCalculator.rosfinanceAPIKey"
      :deautofinancierDealerId="modules.financeCalculator.deautofinancierDealerId"
      :bekaroUniqueKey="modules.financeCalculator.bekaroUniqueKey"
      :financialLeaseDealerId="modules.financeCalculator.financialLeaseDealerId"
      :bdlease="modules.financeCalculator.bdlease"
      :intro-text="modules.financeCalculator.introText"
      :vehicle="vehicle"
      :packages="generalSettings.deliveries"
      :useDeliveryPackages="modules.financeCalculator.useDeliveries"
      @modalClosed="closeModal"
    ></ModalCalculator>

    <!-- MODAL TAXTOOL -->
    <ModalTaxTool
      v-if="modules.taxtool.active"
      :active="activeModal === 'taxTool'"
      :clientId="modules.taxtool.clientId"
      :intro-text="modules.taxtool.introText"
      :vehicle="vehicle"
      @modalClosed="closeModal"
    ></ModalTaxTool>

    <!-- MODAL CALL ME BACK -->
    <ModalCallMeBack
      v-if="modules.callMeBack.active"
      :intro-text="modules.callMeBack.introText"
      :active="activeModal === 'callMeBack'"
      :vehicle="vehicle"
      @modalClosed="closeModal"
    >
    </ModalCallMeBack>

    <!-- MODAL BUY -->
    <ModalBuy
      v-if="modules.buy.active"
      :active="activeModal === 'buy'"
      :packages="generalSettings.deliveries"
      :options="modules.buy.options"
      :intro-text="modules.buy.introText"
      :vehicle="vehicle"
      :useDeliveryPackages="modules.buy.useDeliveries"
      @modalClosed="closeModal"
    ></ModalBuy>
  </div>
</template>

<script>
import ModalTestRide from '@/components/modals/ModalTestRide'
import ModalTaxTool from '@/components/modals/ModalTaxTool'
import ModalCallMeBack from '@/components/modals/ModalCallMeBack'
import ModalCalculator from '@/components/modals/ModalCalculator'
import ModalPrivateLease from '@/components/modals/ModalPrivateLease'
import ModalOffer from '@/components/modals/ModalOffer'
import ModalBuy from '@/components/modals/ModalBuy'
import Slick from 'vue-slick'
import { WPSettings } from '@/WPSettings'
import scrollIt from '@/smoothScroll'
import CoolLightBox from 'vue-cool-lightbox'

import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css'
import ModalAppointment from '@/components/modals/ModalAppointment.vue'

import IconMapPinOutline from '@/components/icons/icon-map-pin-outline.vue'
import IconEnvelopeOutline from '@/components/icons/icon-envelope-outline.vue'
import IconWhatsappOutline from '@/components/icons/icon-whatsapp-outline.vue'

export default {
  components: {
    ModalOffer,
    ModalTaxTool,
    ModalTestRide,
    ModalPrivateLease,
    ModalCallMeBack,
    ModalCalculator,
    ModalBuy,
    Slick,
    CoolLightBox,
    ModalAppointment,
    IconMapPinOutline,
    IconEnvelopeOutline,
    IconWhatsappOutline,
  },
  data() {
    return {
      WPSettings: WPSettings,
      prevRoute: null,
      loading: false,
      modules: this.$store.state.settings.modules,
      generalSettings: this.$store.state.settings.general,
      companyInfo: null,
      vehicle: null,
      error: null,
      activeTab: 'specs',
      activeModal: null,
      warrantyTabActive: false,
      warrantyTextActive: false,
      slickMainOptions: {
        slidesToShow: 1,
        slidesToScroll: 1,
        asNavFor: '.js-slick-nav',
        infinite: true,
        fade: false,
        arrows: true,
        dots: false,
        lazyLoad: 'ondemand',
        swipe: true,
        nextArrow: '<button class="slick-next-button"></button>',
        prevArrow: '<button class="slick-prev-button"></button>',
        mobileFirst: true,
        respondTo: 'slider',
      },
      slickNavOptions: {
        slidesToShow: 4,
        asNavFor: '.js-slick-main',
        slidesToScroll: 1,
        infinite: true,
        focusOnSelect: true,
        arrows: true,
        lazyLoad: 'ondemand',
        waitForAnimate: true,
        nextArrow: '<button class="slick-next-button"></button>',
        prevArrow: '<button class="slick-prev-button"></button>',
      },
      pageUrl: window.location.href,
      lightboxMedia: null,
      lightboxIndex: null,
      slickNavigateTimer: null,
    }
  },
  computed: {
    vehicleSlug: function () {
      const title = this.$options.filters.vehicleTitle(this.vehicle)
      return this.$options.filters.slugify(title)
    },
    getPackagesInLanguage() {
      const output = []
      if (this.generalSettings?.deliveries.length > 0) {
        this.generalSettings.deliveries.forEach((item) => {
          const pack = this.createDeliveryPackage(item)
          output.push(pack)
        })
      }
      return output
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from
    })
  },
  mounted() {
    this.$watch(
      () => {
        return this.$refs.lightbox.imgIndex
      },
      (val) => {
        this.slickGoToImage(val)
      }
    )
  },
  created() {
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData()

    this.prepareLightBox()

    this.fetchCompanyInfo()

    window.addEventListener('resize', (event) => {
      this.$refs.slickMain.reSlick()
    })
  },
  watch: {
    // call the method again  if the route changes
    $route: 'fetchData',
  },
  methods: {
    createDeliveryPackage(item) {
      const output = {
        delivery_price: item.delivery_price,
        delivery_name: item.delivery_name,
        delivery_desc: item.delivery_desc,
      }
      if (item[`delivery_name_${WPSettings.LANGUAGE}`]) {
        output.delivery_name = item[`delivery_name_${WPSettings.LANGUAGE}`]
      }
      if (item[`delivery_desc_${WPSettings.LANGUAGE}`]) {
        output.delivery_desc = item[`delivery_desc_${WPSettings.LANGUAGE}`]
      }
      return output
    },
    fetchData() {
      this.error = this.vehicle = null
      const vehicleId = this.$route.params.id
      const vehicleSlugInUrl = this.$route.params.slug
      this.$store.commit('vehicles/updateCurrentVehicle', vehicleId)
      this.vehicle = this.$store.getters['vehicles/current']
      // Reroute if vehicle slug is not defined or incorrect
      const vehicleSlug = this.$options.filters.slugify(this.$options.filters.vehicleTitle(this.vehicle))

      if (!vehicleSlugInUrl || vehicleSlugInUrl !== vehicleSlug) {
        this.$router.replace({ path: '/voertuig/' + vehicleId + '/' + vehicleSlug })
      }

      // Set document title
      document.title =
        this.vehicle.properties.brand +
        ' ' +
        this.vehicle.properties.model +
        ' ' +
        this.vehicle.properties.type +
        ' - Occasions'

      // Check if warranty tab should be active
      for (const warranty in this.vehicle.properties.warranty) {
        if (this.vehicle.properties.warranty[warranty]) {
          this.warrantyTabActive = true
          break
        }
      }

      // Check if warranty tab should be active
      const warrantyTextKeys = [
        'bovag40puntencheck',
        'bovagAfleverbeurt',
        'bovagGarantie6',
        'bovagGarantie12',
        'bovagGarantie24',
        'autotrust',
        'vakgarant',
        'vweOccasionGarant',
        'cargarantie',
      ]
      for (const warranty of warrantyTextKeys) {
        if (this.vehicle.properties.warranty[warranty] && this.vehicle.properties.warranty[warranty] === true) {
          this.warrantyTextActive = true
          break
        }
      }
    },

    setTab(id) {
      this.activeTab = id
    },

    openModal(modalName) {
      this.activeModal = modalName
    },

    closeModal() {
      this.activeModal = ''
    },

    scrollToActions() {
      scrollIt(this.$refs.actionButtons, 500, 'easeInOutCubic', 30)
    },
    addVehicle(vehicle) {
      this.$store.commit('compare/addVehicle', vehicle)
      this.$router.push({ path: '/vergelijken' })
    },
    navigateBack() {
      if (this.prevRoute.name === 'home') {
        this.$router.go(-1)
      } else {
        this.$router.push('/')
      }
    },

    slickBeforeChange(event, slick, currentSlide, nextSlide) {
      if (currentSlide === nextSlide) return

      const youtubeIframe = document.querySelector('.slick-current .slick-item-youtube iframe')
      if (youtubeIframe) {
        youtubeIframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*')
      }

      const taggleIframe = document.querySelector('.slick-current .slick-item-taggle iframe')
      if (taggleIframe) {
        setTimeout(() => {
          const taggleSrc = taggleIframe.src
          taggleIframe.src = taggleSrc
        }, 500)
      }
    },
    prepareLightBox() {
      const data = []

      for (const image of this.vehicle.images) {
        data.push({
          src: image,
        })
      }

      this.lightboxMedia = data
    },
    openGallery(index) {
      this.lightboxIndex = index
      setTimeout(() => {
        const fragment = document.createDocumentFragment()
        const node = document.getElementsByClassName('cool-lightbox')[0]
        fragment.appendChild(node)
        document.body.appendChild(fragment)
      }, 200)
    },
    slickGoToImage(index) {
      // Add a timer as debounce
      clearTimeout(this.slickNavigateTimer)
      if (index) {
        this.slickNavigateTimer = setTimeout(() => {
          // Navigate the sliders to index
          this.$refs.slickMain.goTo(index)
          this.$refs.slickNav.goTo(index)
        }, 500)
      }
    },
    fetchCompanyInfo() {
      // Get customer id of current vehicle
      const vehicleCustomerId = this.vehicle.customerId ?? 'default'

      // Fetch the company info from settings
      const companyInfo = this.generalSettings.companyInfo.find((row) => row.customerId === vehicleCustomerId)

      // Company info is found, save and return
      if (companyInfo) {
        this.companyInfo = companyInfo
        return
      }

      // Nothing found, return default company info
      this.companyInfo = this.generalSettings.companyInfo.find((row) => row.customerId === 'default')
    },
    printVehicle() {
      window.print()
    },
  },
}
</script>
